import React, { useRef, useState, useEffect, useCallback, memo } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import Supercluster from 'supercluster';
import { Business } from '@/types/homepage';
import { cn } from '@/lib/utils';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import type { Feature, Point } from 'geojson';
// Define map bounds interface
interface MapBounds {
  northEast: { latitude: number; longitude: number; };
  southWest: { latitude: number; longitude: number; };
  currentZoom?: number;
}

// Define types for our cluster properties
interface BusinessProperties {
  id: string;
  name: string;
  description: string;
  emoji: string;
  hasAnimalActivities: boolean;
  hasAdventureActivities: boolean;
}

type ClusterFeature = Feature<Point> & {
  properties: BusinessProperties & {
    cluster?: boolean;
    cluster_id?: number;
    point_count?: number;
    point_count_abbreviated?: string;
  };
};

const SMALL_PIN_MAX_ZOOM = 6;

interface MapLibreMapProps {
  businesses: Business[];
  selectedBusiness?: Business | null;
  onBusinessSelect?: (business: Business) => void;
  onBoundsChange?: (bounds: MapBounds) => void;
  onMapReady?: (map: maplibregl.Map) => void;
  className?: string;
  style?: React.CSSProperties;
  height?: string;
  /** Group nearby operators into numbered clusters. Off shows one pin per operator. */
  clusterMarkers?: boolean;
  externalMapStatus?: {
    state: string;
    lastUpdateTime?: number;
    updateMapStatus?: (updates: any) => void;
  };
}

function MapLibreMapComponent({
  businesses = [],
  selectedBusiness,
  onBusinessSelect,
  onBoundsChange,
  onMapReady,
  className,
  style,
  height = '500px',
  externalMapStatus,
  clusterMarkers = true,
}: MapLibreMapProps) {
  // Log business count changes for debugging
  useEffect(() => {
    // LOGGING DISABLED FOR PRODUCTION
    // console.log('[MapLibreMap] Business count changed:', businesses.length);
  }, [businesses.length]);

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markers = useRef<{ [key: string]: maplibregl.Marker }>({});
  const supercluster = useRef<Supercluster>();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const isInitializing = useRef(false);
  // Guards async map callbacks (load/error/timeout) against firing after the
  // component has already unmounted, when refs like mapContainer.current are stale.
  const isMountedRef = useRef(false);

  // Store reference to interaction handler for cleanup
  const mapInteractionHandler = useRef<(() => void) | null>(null);
  
  // Use refs for callbacks to avoid dependency issues
  const onMapReadyRef = useRef(onMapReady);
  const onBoundsChangeRef = useRef(onBoundsChange);
  const onBusinessSelectRef = useRef(onBusinessSelect);
  
  // Update refs when props change
  useEffect(() => {
    onMapReadyRef.current = onMapReady;
  }, [onMapReady]);
  
  useEffect(() => {
    onBoundsChangeRef.current = onBoundsChange;
  }, [onBoundsChange]);
  
  useEffect(() => {
    onBusinessSelectRef.current = onBusinessSelect;
  }, [onBusinessSelect]);

  // Add a ref to track if we're in a programmatic movement
  const isProgrammaticMove = useRef(false);

  // Function to create custom marker element with enhanced styling (following TestMapPage pattern)
  const createMarkerElement = useCallback((emoji: string, count?: number, isSelected: boolean = false, isSmall: boolean = false) => {
    const markerElement = document.createElement('div');
    markerElement.className = 'custom-marker';

    // Follow TestMapPage styling exactly for consistency
    markerElement.style.width = isSmall ? '30px' : (count ? '50px' : '44px');
    markerElement.style.height = isSmall ? '30px' : (count ? '50px' : '44px');
    markerElement.style.backgroundColor = count ? '#2C5F2D' : (isSelected ? '#FF6B35' : '#ffffff');
    markerElement.style.borderRadius = '50%';
    markerElement.style.border = isSmall
      ? `2px solid ${isSelected ? '#ffffff' : '#2C5F2D'}`
      : `3px solid ${count ? '#ffffff' : (isSelected ? '#ffffff' : '#2C5F2D')}`;
    markerElement.style.display = 'flex';
    markerElement.style.alignItems = 'center';
    markerElement.style.justifyContent = 'center';
    markerElement.style.fontSize = isSmall ? '16px' : (count ? '16px' : '24px');
    markerElement.style.color = count ? '#ffffff' : '#000000';
    markerElement.style.cursor = 'pointer';
    markerElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
    
    // Remove problematic transform transitions that cause positioning issues
    // markerElement.style.transition = 'transform 0.2s ease-in-out'; // REMOVED
    
    markerElement.innerHTML = count ? `${count}` : (emoji || '📍');

    // Simplified hover effects without transform to prevent positioning issues
    markerElement.addEventListener('mouseenter', () => {
      markerElement.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
      markerElement.style.zIndex = '1000';
    });
    
    markerElement.addEventListener('mouseleave', () => {
      markerElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
      markerElement.style.zIndex = 'auto';
    });
    
    return markerElement;
  }, []);

  // Enhanced coordinate validation function
  const validateBusinessCoordinates = useCallback((business: Business): boolean => {
    if (!business.location?.coordinates?.coordinates) {
      console.warn(`[MapLibreMap] Business ${business._id} has no coordinates`);
      return false;
    }

    const coords = business.location.coordinates.coordinates;
    if (!Array.isArray(coords) || coords.length !== 2) {
      console.warn(`[MapLibreMap] Business ${business._id} has invalid coordinate format:`, coords);
      return false;
    }

    const [lng, lat] = coords;
    
    // Check for valid coordinate ranges
    if (typeof lng !== 'number' || typeof lat !== 'number') {
      console.warn(`[MapLibreMap] Business ${business._id} has non-numeric coordinates:`, coords);
      return false;
    }

    if (Math.abs(lng) > 180 || Math.abs(lat) > 90) {
      console.warn(`[MapLibreMap] Business ${business._id} has coordinates outside valid ranges:`, coords);
      return false;
    }

    // Check for obviously invalid coordinates (0,0 or very close to 0,0)
    if (Math.abs(lng) < 0.001 && Math.abs(lat) < 0.001) {
      console.warn(`[MapLibreMap] Business ${business._id} has coordinates too close to (0,0):`, coords);
      return false;
    }

    return true;
  }, []);

  // Enhanced updateMarkers function with better error handling and validation
  const updateMarkers = useCallback((businesses: Business[], mapInstance: maplibregl.Map) => {
    // DEBUG LOGGING DISABLED FOR PRODUCTION
    // console.log(`[MapLibreMap][DEBUG] updateMarkers called with ${businesses.length} businesses`);
    
    // Debug first few businesses structure (DISABLED)
    // if (businesses.length > 0) {
    //   console.log('[MapLibreMap][DEBUG] First 2 businesses:', businesses.slice(0, 2).map(b => ({
    //     id: b._id,
    //     name: b.name,
    //     coordinates: b.location?.coordinates?.coordinates,
    //     hasValidCoords: validateBusinessCoordinates(b)
    //   })));
    // }
    
    if (!mapInstance) {
      // console.warn('[MapLibreMap][DEBUG] updateMarkers called without valid map instance');
      return;
    }

    // Safely remove old markers with error handling
    try {
      const markerKeys = Object.keys(markers.current);
      // console.log(`[MapLibreMap][DEBUG] Removing ${markerKeys.length} existing markers`);
      
      markerKeys.forEach(key => {
        try {
          const marker = markers.current[key];
          if (marker && typeof marker.remove === 'function') {
            marker.remove();
          }
        } catch (error) {
          console.warn(`[MapLibreMap][DEBUG] Error removing marker ${key}:`, error);
        }
      });
      
      markers.current = {};
    } catch (error) {
      console.error('[MapLibreMap][DEBUG] Error during marker cleanup:', error);
      markers.current = {};
    }

    // Filter and validate businesses
    const validBusinesses = businesses.filter(validateBusinessCoordinates);
    // console.log(`[MapLibreMap][DEBUG] Validation results:`, {
    //   total: businesses.length,
    //   valid: validBusinesses.length,
    //   invalid: businesses.length - validBusinesses.length
    // });

    if (validBusinesses.length === 0) {
      // console.warn('[MapLibreMap][DEBUG] No businesses with valid coordinates to display');
      return;
    }

    try {
      // Initialize supercluster if not already done
      if (!supercluster.current) {
        supercluster.current = new Supercluster({
          radius: 40,
          maxZoom: 16,
          minPoints: 2
        });
      }

      // Convert businesses to GeoJSON features
      const features: ClusterFeature[] = validBusinesses.map(business => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: business.location.coordinates.coordinates
        },
        properties: {
          id: business._id,
          name: business.name,
          description: business.description,
          emoji: business.emoji || '📍',
          hasAnimalActivities: business.hasAnimalActivities || false,
          hasAdventureActivities: business.hasAdventureActivities || false
        }
      }));

      // Load features into supercluster
      supercluster.current.load(features);

      // Get current zoom
      const zoom =Math.floor(Math.max(0, Math.min(20, mapInstance.getZoom())));

      // console.log(`[MapLibreMap][DEBUG] Current map state:`, {
      //   bounds: {
      //     west: bounds.getWest(),
      //     south: bounds.getSouth(),
      //     east: bounds.getEast(),
      //     north: bounds.getNorth()
      //   },
      //   zoom,
      //   center: mapInstance.getCenter()
      // });

      // Get clusters and create markers - now with null safety
      if (!supercluster.current) {
        console.error('[MapLibreMap] Supercluster is still undefined after initialization');
        return;
      }

      // Pins overlap when zoomed out, so use smaller pins until there is room for the full size
      const useSmallPins = !clusterMarkers && zoom < SMALL_PIN_MAX_ZOOM;

      // Every operator stays on the map; the view only decides what the list shows
      const clusters: Array<Supercluster.ClusterFeature<Supercluster.AnyProps> | Supercluster.PointFeature<Supercluster.AnyProps>> = clusterMarkers
        ? supercluster.current.getClusters([-180, -85, 180, 85], zoom)
        : features;

      // console.log(`[MapLibreMap][DEBUG] Creating markers:`, {
      //   totalClusters: clusters.length,
      //   individualMarkers: clusters.filter(c => !c.properties.cluster).length,
      //   clusterMarkers: clusters.filter(c => c.properties.cluster).length
      // });

      // Create markers for clusters and individual points
      clusters.forEach((cluster) => {
        const [longitude, latitude] = cluster.geometry.coordinates;
        const { cluster: isCluster, point_count: pointCount } = cluster.properties;

        if (isCluster) {
          // Create cluster marker with proper MapLibre Marker instance
          const clusterElement = createMarkerElement('', pointCount);
          
          const clusterMarker = new maplibregl.Marker({
            element: clusterElement,
            anchor: 'center'
          })
            .setLngLat([longitude, latitude])
            .addTo(mapInstance);

          // Add click handler for cluster expansion
          clusterElement.addEventListener('click', () => {
            if (!supercluster.current || !cluster.properties.cluster_id) return;
            
            try {
              const expansionZoom = Math.min(
                supercluster.current.getClusterExpansionZoom(cluster.properties.cluster_id),
                20
              );
              
              mapInstance.flyTo({
                center: [longitude, latitude],
                zoom: expansionZoom,
                speed: 1.5
              });
            } catch (expansionError) {
              console.warn('[MapLibreMap] Error expanding cluster:', expansionError);
            }
          });

          const markerId = `cluster-${cluster.properties.cluster_id || `${longitude}-${latitude}`}`;
          markers.current[markerId] = clusterMarker;
        } else {
          // Create individual marker
          const { properties } = cluster;
          const isSelected = !!(selectedBusiness && selectedBusiness._id === properties.id);
          const markerElement = createMarkerElement(properties.emoji, undefined, isSelected, useSmallPins);
          markerElement.title = properties.name;

          const marker = new maplibregl.Marker({
            element: markerElement,
            anchor: 'center'
          })
            .setLngLat([longitude, latitude])
            .addTo(mapInstance);

          // Add click handler for individual markers
          markerElement.addEventListener('click', () => {
            const business = validBusinesses.find(b => b._id === properties.id);
            if (business && onBusinessSelectRef.current) {
              onBusinessSelectRef.current(business);
            }
          });

          const markerId = `business-${properties.id}`;
          markers.current[markerId] = marker;
        }
      });

      // console.log('[MapLibreMap][DEBUG] Markers created successfully');

    } catch (error) {
      console.error('[MapLibreMap] Error creating markers:', error);
    }
  }, [businesses, selectedBusiness, mapLoaded, onBusinessSelectRef, clusterMarkers]);

  // Clusters and pin sizes depend on the zoom level
  useEffect(() => {
    const mapInstance = map.current;
    if (!mapInstance || !mapLoaded) return;

    const refreshMarkers = () => updateMarkers(businesses, mapInstance);
    mapInstance.on('zoomend', refreshMarkers);
    return () => {
      mapInstance.off('zoomend', refreshMarkers);
    };
  }, [mapLoaded, businesses, updateMarkers]);

  // Track container mounting with reduced logging
  useEffect(() => {
    // Only log if there's an issue
    if (!mapContainer.current) {
      // console.log('[MapLibreMap] Container not available after mount');
    }
  }, []);

  // Initialize map with a small delay to ensure container is ready
  useEffect(() => {
    isMountedRef.current = true;
    let initialTimer: ReturnType<typeof setTimeout> | null = null;

    const initializeMap = () => {
      if (!isMountedRef.current) {
        return;
      }

      if (!mapContainer.current) {
        // console.log('[MapLibreMap] No map container - skipping initialization');
        return;
      }

      if (isInitializing.current) {
        // console.log('[MapLibreMap] Already initializing - skipping');
        return;
      }

      if (map.current) {
        // console.log('[MapLibreMap] Map already exists - skipping initialization');
        return;
      }

      // Check if container has dimensions
      const containerRect = mapContainer.current.getBoundingClientRect();

      if (containerRect.width === 0 || containerRect.height === 0) {
        // console.warn('[MapLibreMap] Container has zero dimensions, delaying initialization');
        setTimeout(() => {
          if (isMountedRef.current) {
            initializeMap();
          }
        }, 100);
        return;
      }

      isInitializing.current = true;
      // console.log('[MapLibreMap] Starting map initialization...');

      try {
        const mapInstance = new maplibregl.Map({
          container: mapContainer.current,
          style: {
            version: 8,
            sources: {
              'osm': {
                type: 'raster',
                tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
                tileSize: 256,
                attribution: '© OpenStreetMap contributors'
              }
            },
            layers: [
              {
                id: 'osm',
                type: 'raster',
                source: 'osm',
                minzoom: 0,
                maxzoom: 19
              }
            ]
          },
          center: [-3.7492, 40.4637], // Spain center
          zoom: 6
        });
        // console.log('[MapLibreMap] Map instance created successfully');

        // Add timeout to detect if load event never fires
        const loadTimeout = setTimeout(() => {
          if (!isMountedRef.current) return;
          console.error('[MapLibreMap] Map load timeout - load event never fired');
          setInitError('Map failed to load within timeout period');
          isInitializing.current = false;
        }, 10000); // 10 second timeout

        // Handle errors
        mapInstance.on('error', (e) => {
          console.error('[MapLibreMap] Map error event:', e);
          clearTimeout(loadTimeout);
          if (!isMountedRef.current) return;
          setInitError(`Map error: ${e.error?.message || 'Unknown error'}`);
          isInitializing.current = false;
        });

        // Handle successful load
        mapInstance.on('load', () => {
          clearTimeout(loadTimeout);
          // console.log('[MapLibreMap] Map loaded successfully!');

          // The component may have unmounted while tiles were loading. In that case
          // there's nothing stale to recover — just tear down this orphaned instance
          // quietly instead of surfacing a false "failed to initialize" error.
          if (!isMountedRef.current) {
            try { mapInstance.remove(); } catch { /* already gone */ }
            return;
          }

          // Ensure map instance is valid before proceeding
          if (!mapInstance || !mapContainer.current) {
            console.error('[MapLibreMap] Invalid map instance or container after load');
            setInitError('Map failed to initialize properly');
            isInitializing.current = false;
            return;
          }

          map.current = mapInstance;
          
          // Add custom flyTo method to prevent race conditions
          (mapInstance as any).customFlyTo = customFlyTo;
          
          // Add navigation controls (attribution is already handled by source config)
          try {
            mapInstance.addControl(new maplibregl.NavigationControl(), 'top-right');
            // Note: Attribution is already configured in the source, no need to add separate control
          } catch (controlError) {
            console.error('[MapLibreMap] Error adding controls:', controlError);
          }
          
          // Wait for next frame to ensure map is fully rendered
          requestAnimationFrame(() => {
            // Set map as loaded
            setMapLoaded(true);
            
            // Notify parent of map ready state
            try {
              // console.log('[MapLibreMap] Notifying parent of map ready state');
              if (onMapReadyRef.current && mapInstance) {
                onMapReadyRef.current(mapInstance);
              } else {
                // console.warn('[MapLibreMap] No onMapReady callback or invalid map instance');
              }
            } catch (callbackError) {
              console.error('[MapLibreMap] Error in onMapReady callback:', callbackError);
            }
            
            // Set up event listeners for user interactions
            const handleMapInteraction = () => {
              // Skip if this is a programmatic move
              if (isProgrammaticMove.current) {
                // console.log('[MapLibreMap][INTERACTION] Skipping bounds change - programmatic move');
                return;
              }
              
              if (onBoundsChangeRef.current && mapInstance) {
                try {
                  const bounds = mapInstance.getBounds();
                  const mapBounds: MapBounds = {
                    northEast: {
                      latitude: bounds.getNorthEast().lat,
                      longitude: bounds.getNorthEast().lng
                    },
                    southWest: {
                      latitude: bounds.getSouthWest().lat,
                      longitude: bounds.getSouthWest().lng
                    }
                  };
                  // console.log('[MapLibreMap][INTERACTION] User interaction bounds change:', {
                  //   bounds: mapBounds,
                  //   center: mapInstance.getCenter(),
                  //   zoom: mapInstance.getZoom(),
                  //   timestamp: new Date().toISOString()
                  // });
                  onBoundsChangeRef.current(mapBounds);
                } catch (boundsError) {
                  console.error('[MapLibreMap][INTERACTION] Error getting bounds after interaction:', boundsError);
                }
              }
            };
            
            // Add event listeners for map interactions
            mapInteractionHandler.current = handleMapInteraction;
            mapInstance.on('moveend', handleMapInteraction);
            mapInstance.on('zoomend', handleMapInteraction);
            
            // console.log('[MapLibreMap] Set up user interaction listeners');
            
            // Trigger initial bounds change to load businesses
            if (onBoundsChangeRef.current && mapInstance) {
              try {
                const bounds = mapInstance.getBounds();
                const mapBounds: MapBounds = {
                  northEast: {
                    latitude: bounds.getNorthEast().lat,
                    longitude: bounds.getNorthEast().lng
                  },
                  southWest: {
                    latitude: bounds.getSouthWest().lat,
                    longitude: bounds.getSouthWest().lng
                  }
                };
                // console.log('[MapLibreMap] Triggering initial bounds change:', mapBounds);
                onBoundsChangeRef.current(mapBounds);
              } catch (boundsError) {
                console.error('[MapLibreMap] Error getting initial bounds:', boundsError);
              }
            }
          });
        });

      } catch (error) {
        console.error('[MapLibreMap] Failed to initialize map:', error);
        if (isMountedRef.current) {
          setInitError(`Failed to initialize map: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
        isInitializing.current = false;
      }
    };

    // Only initialize once when component mounts
    if (!map.current && !isInitializing.current) {
      initialTimer = setTimeout(initializeMap, 100);
    }

    // Single, unconditional cleanup: always cancel the pending init timer (if the
    // component unmounts before it fires) AND always tear down a live map instance
    // (if init already completed). Previously these were two separate branches
    // where only one ever ran, so a map created after the initial mount was never
    // actually removed on unmount — leaving an orphaned instance whose async
    // 'load'/'error' callbacks could still fire later against stale refs.
    return () => {
      isMountedRef.current = false;

      if (initialTimer) {
        clearTimeout(initialTimer);
      }

      Object.values(markers.current).forEach(marker => {
        try { marker.remove(); } catch { /* already gone */ }
      });
      markers.current = {};

      if (map.current) {
        try {
          if (mapInteractionHandler.current) {
            map.current.off('moveend', mapInteractionHandler.current);
            map.current.off('zoomend', mapInteractionHandler.current);
          }
          map.current.remove();
        } catch (error) {
          // console.warn('[MapLibreMap] Error removing map on cleanup:', error);
        }
        map.current = null;
      }
      isInitializing.current = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Reset supercluster when business data changes
  useEffect(() => {
    // Reset supercluster when businesses change to ensure fresh clustering
    if (supercluster.current && businesses.length > 0) {
      // Clear the existing supercluster to force reinitialization with new data
      supercluster.current = undefined;
    }
  }, [businesses]);

  // Simple marker update effect like TestMapPage
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      // console.log('[MapLibreMap][DEBUG] Skipping marker update - map not ready:', {
      //   mapLoaded,
      //   hasMapInstance: !!map.current
      // });
      return;
    }

    // console.log('[MapLibreMap][DEBUG] Marker update triggered:', {
    //   businessCount: businesses.length,
    //   selectedBusinessId: selectedBusiness?._id
    // });
    
    try {
      updateMarkers(businesses, map.current);
    } catch (error) {
      console.error('[MapLibreMap][DEBUG] Error in marker update effect:', error);
    }
  }, [mapLoaded, businesses, selectedBusiness, updateMarkers]);

  // Enhanced flyTo method that prevents race conditions
  const customFlyTo = useCallback((center: [number, number], zoom: number, options: any = {}) => {
    if (!map.current) return;
    
    // console.log('[MapLibreMap] Starting programmatic flyTo');
    isProgrammaticMove.current = true;
    
    map.current.flyTo({
      center,
      zoom,
      duration: options.duration || 2000,
      essential: true,
      ...options
    });
    
    // Reset programmatic flag after animation completes
    const resetFlag = () => {
      // console.log('[MapLibreMap] Programmatic flyTo completed');
      isProgrammaticMove.current = false;
      map.current?.off('moveend', resetFlag);
    };
    
    map.current.once('moveend', resetFlag);
  }, []);

  // Show error state if initialization failed
  if (initError) {
    console.error('[MapLibreMap] Rendering error state:', initError);
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 rounded-xl p-8">
        <div className="text-center max-w-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Map Error
          </h3>
          <p className="text-gray-600 mb-4">
            {initError}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // Only render container when actually needed
  return (
    <ErrorBoundary fallback={
      <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-xl">
        <p className="text-gray-500">Map failed to load</p>
      </div>
    }>
      <div 
        ref={mapContainer} 
        className={cn(
          "relative w-full rounded-lg overflow-hidden",
          "transition-all duration-300 ease-in-out",
          className
        )}
        style={{
          height: height || '100%',
          width: '100%',
          position: 'relative',
          ...style
        }}
      />
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">Loading map...</p>
            <p className="text-gray-400 text-xs mt-1">
              Container: {mapContainer.current ? 
                `${mapContainer.current.clientWidth}x${mapContainer.current.clientHeight}` : 
                'Not mounted'}
            </p>
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
}

// Memoized version to prevent unnecessary re-renders
export const MapLibreMap = memo(MapLibreMapComponent, (prevProps, nextProps) => {
  // Compare all props except callback functions (they are handled via refs)
  return (
    prevProps.businesses.length === nextProps.businesses.length &&
    prevProps.selectedBusiness?._id === nextProps.selectedBusiness?._id &&
    prevProps.className === nextProps.className &&
    prevProps.height === nextProps.height &&
    prevProps.clusterMarkers === nextProps.clusterMarkers &&
    prevProps.externalMapStatus?.state === nextProps.externalMapStatus?.state &&
    JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style) &&
    // Compare businesses array content
    JSON.stringify(prevProps.businesses.map(b => b._id)) === JSON.stringify(nextProps.businesses.map(b => b._id))
  );
});

export default MapLibreMap; 